import { IconButton, makeStyles, Tooltip, Typography, useTranslation, WidthToggleIcon } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { RenderableItem } from '@paintscout/util/builder'
import { getObjectLabels, getQuoteOptions } from '@paintscout/util/builder'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import React from 'react'
import { useQuote } from '../context'
import { useTranslatedNoteContent } from '../Quote/views/WorkOrderView/useTranslatedNoteContent'
import { translateStructuredWorkOrderContent } from '../Quote/views/WorkOrderView/WorkOrderCard/translateStructuredWorkOrderContent'
import WorkOrderItemRows, { formatHourTotal, getItemHourTotals, resolveName } from './WorkOrderItemRows'
import WorkOrderTotalFooter from './WorkOrderTotalFooter'

export interface WorkOrderCardNextProps {
  item: RenderableItem
  /** Show the per-row hour values (respects the section's "Show all hours" setting). */
  showHours?: boolean
  /** Show the "Total → {labour}: X" footer. */
  showTotalHours?: boolean
  /** Hover-revealed drag handle (a react-sortable-hoc SortableHandle supplied by the section). */
  dragHandle?: React.ReactNode
  /** Whether this card currently spans both grid columns (drives the width-toggle icon state). */
  isFullWidth?: boolean
  /** Hide this card into the section's "Hidden" panel. */
  onToggleHide?: () => void
  /** Toggle this card between half- and full-width. */
  onToggleWidth?: () => void
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    // The card's edge is the heavier P85; the lighter rules between rows live on WorkOrderItemRows.
    const outline = `1px solid ${neutral.P85}`
    return {
      // One bordered stack — header, rows, then the Total bar in its own tray. `xl` (12px) is the
      // closest token to the mock's 10px corners; `overflow: hidden` clips the header and tray to it.
      root: {
        border: outline,
        borderRadius: theme.borderRadius.xl,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        '&:hover $dragHandleWrap, &:hover $controls, &:hover $controlsDivider': {
          opacity: 1
        },
        // Hovering anywhere on the card tints its rows, so it reads as one target.
        '&:hover $row, &:hover $rowDivider, &:hover $footerTray': {
          backgroundColor: neutral.P98
        }
      },
      // Light-gray card header: name on the left, the "hr" unit on the right (matches the mock).
      // Relative so the drag handle can be absolutely centered without shifting the name. No bottom
      // border — the first row carries the outline-weight rule instead.
      header: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        minHeight: 44,
        padding: theme.spacing(1.25, 2),
        backgroundColor: neutral.P96
      },
      // Drag handle, centered and revealed on card hover.
      dragHandleWrap: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        color: neutral.P60,
        cursor: 'grab',
        opacity: 0,
        transition: 'opacity 120ms ease',
        '& svg': { fontSize: 18 }
      },
      headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexShrink: 0,
        zIndex: 1
      },
      // Hide + width controls, revealed on card hover (the hr label stays visible).
      controls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        opacity: 0,
        transition: 'opacity 120ms ease'
      },
      controlButton: {
        padding: 1,
        color: neutral.P60,
        '& svg': { fontSize: 18 }
      },
      // Separates the controls from the "hr" label, and fades in with them.
      controlsDivider: {
        width: 1,
        height: 22,
        backgroundColor: neutral.P85,
        flexShrink: 0,
        opacity: 0,
        transition: 'opacity 120ms ease'
      },
      name: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(15),
        color: neutral.P35,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      subtitle: {
        color: neutral.P60,
        fontWeight: 400,
        marginLeft: theme.spacing(0.5)
      },
      hr: {
        color: neutral.P60,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(16),
        flexShrink: 0
      },
      body: {},
      // Layout for the rows/divider/footer bar lives on the shared components (WorkOrderItemRows,
      // WorkOrderTotalFooter); these classes are the card's own additions, handed to them so the
      // hover-tint rules above can still reach those elements.
      row: {
        // Outline-weight rule under the header; the rules between rows are inset (see rowDivider).
        '&:first-child': {
          borderTop: outline
        }
      },
      rowDivider: {
        transition: 'background-color 150ms ease'
      },
      footerTray: {
        transition: 'background-color 150ms ease'
      }
    }
  },
  { name: 'WorkOrderCardNext' }
)

/**
 * The redesigned Work Order hour card (estimateNext) — a light-header card matching the mock: the
 * area/substrate name + "hr" unit in the header, then one row per sub-item (name, location, Coats)
 * with its hours on the right, and a "Total → {labour}: X" footer. Hovering the header reveals a
 * center drag handle plus hide / half-full-width controls (supplied by the parent section).
 */
export default function WorkOrderCardNext({
  item,
  showHours,
  showTotalHours,
  dragHandle,
  isFullWidth,
  onToggleHide,
  onToggleWidth,
  onClick
}: WorkOrderCardNextProps) {
  const classes = useStyles()
  const { __ } = useTranslation()
  const { quote } = useQuote()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { hideWorkOrderDimensions } = getQuoteOptions({ quote, options })

  const name = resolveName(item.name)
  const subItems = item.subItems ?? []

  // The card's own name plus the two account-configurable footer labels, all user content, so they go
  // through the live note-translation batch (customer view) / pre-baked map like the rows do. The rows
  // translate their own content in `WorkOrderItemRows` — the hook batches across instances.
  const [translatedInputs] = useTranslatedNoteContent([name, objectLabels.labour.value, objectLabels.prep.value])
  const translatedName = translateStructuredWorkOrderContent(translatedInputs[0] || name, __)
  const translatedLabour = translateStructuredWorkOrderContent(translatedInputs[1] || objectLabels.labour.value, __)
  const translatedPrep = translateStructuredWorkOrderContent(translatedInputs[2] || objectLabels.prep.value, __)

  const { prep: prepTotal, painting: paintingTotal, total: hoursTotal } = getItemHourTotals(item)
  // The split only earns its place when it actually reconciles with the total. It won't when the
  // item's hours are overridden (`item.value` is authoritative and wins), and three numbers that
  // don't add up read as a bug to a crew — so in that case the footer shows the total alone.
  const splitAddsUp = Math.abs(prepTotal + paintingTotal - hoursTotal) < 0.005
  const footerParts = [
    ...(splitAddsUp
      ? [`${translatedPrep}: ${formatHourTotal(prepTotal)}`, `${translatedLabour}: ${formatHourTotal(paintingTotal)}`]
      : []),
    `${__('Total')}: ${formatHourTotal(hoursTotal)}`
  ]

  const showTotal =
    showTotalHours &&
    (subItems.length > 1 ||
      (item?.value !== 0 &&
        item?.subItemTotal != null &&
        Number(item?.value).toFixed(2) !== Number(item?.subItemTotal).toFixed(2)))

  return (
    <div className={classes.root} onClick={onClick} role={onClick ? 'button' : undefined}>
      <div className={classes.header}>
        <Typography className={classes.name}>
          {translatedName}
          {item.subtitle && !hideWorkOrderDimensions && <span className={classes.subtitle}>({item.subtitle})</span>}
        </Typography>
        {dragHandle && (
          // `disableWrapper` matters here: Tooltip otherwise wraps children in a MUI Badge, whose
          // root is `position: relative`, so the handle's absolute centering would resolve against
          // that zero-width Badge (mid-flex-row) instead of the header — drifting with title length.
          <Tooltip content="Drag to reorder" placement="top" hideIcon disableWrapper>
            <span className={classes.dragHandleWrap}>{dragHandle}</span>
          </Tooltip>
        )}
        <div className={classes.headerRight}>
          <span className={classes.controls}>
            {onToggleHide && (
              <IconButton
                size="small"
                className={classes.controlButton}
                aria-label="Hide card"
                onClick={(ev) => {
                  ev.stopPropagation()
                  onToggleHide()
                }}
              >
                <VisibilityOffIcon />
              </IconButton>
            )}
            {onToggleWidth && (
              <IconButton
                size="small"
                className={classes.controlButton}
                aria-label={isFullWidth ? 'Make card half width' : 'Make card full width'}
                onClick={(ev) => {
                  ev.stopPropagation()
                  onToggleWidth()
                }}
              >
                <WidthToggleIcon />
              </IconButton>
            )}
          </span>
          {(onToggleHide || onToggleWidth) && <span className={classes.controlsDivider} />}
          <span className={classes.hr}>hr</span>
        </div>
      </div>

      <div className={classes.body}>
        <WorkOrderItemRows
          item={item}
          showHours={showHours}
          rowClassName={classes.row}
          dividerClassName={classes.rowDivider}
        />
      </div>

      {showTotal && <WorkOrderTotalFooter className={classes.footerTray} label={__('Total')} parts={footerParts} />}
    </div>
  )
}
